import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { DirLeadStatusType, LeadCounterRowType } from '@econtractor/types';
import * as apiLead from 'api/apiLeads';
import { useGlobal } from 'context/GlobalContext';
import { useSettings } from 'context/SettingsContext';
import { useToast } from 'context/ToastContext';
import SpinnerBlock from 'components/others/SpinnerBlock';
import { useLead } from '../Leads/context/LeadProvider';
import LeadDashboardCard from './LeadDashboardCard';

const countsByStatus = (rows: LeadCounterRowType[] | null): Record<number, number> => {
    if (!rows) return {};
    return rows.reduce<Record<number, number>>((acc, row) => {
        acc[Number(row.leadStatus)] = Number(row.CNT || 0);
        return acc;
    }, {});
};

const LeadDashboard = () => {
    const { t } = useTranslation(['Leads', 'common']);
    const { g } = useGlobal();
    const { showToast } = useToast();
    const { localSettings } = useSettings();
    const { leadsDateFilter, listRefreshNonce } = useLead();
    const location = useLocation();
    const statuses = localSettings?.DIRS?.LeadStatus || [];
    const countRows = g?.paginator?.[location.pathname]?.countRows;
    const dateFrom = leadsDateFilter.dates?.[0] ?? null;
    const dateTo = leadsDateFilter.dates?.[1] ?? null;

    const [counts, setCounts] = useState<Record<number, number> | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            const res = await apiLead.getStatusCounts(
                dateFrom && dateTo ? { dateFrom, dateTo } : {},
            );
            if (cancelled) return;
            setLoading(false);
            if (!res) {
                showToast(t('common:messError'), 'danger');
                return;
            }
            setCounts(countsByStatus(res.counter));
        };

        void load();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateFrom, dateTo, listRefreshNonce, countRows]);

    if (!statuses.length) return null;

    return (
        <div className="leads-dashboard position-relative">
            <SpinnerBlock onlyBlur={true} show={loading} />
            {statuses.map((status: DirLeadStatusType) => (
                <LeadDashboardCard
                    key={status.ID}
                    status={status}
                    count={counts ? counts[status.ID] || 0 : null}
                />
            ))}
        </div>
    );
};

export default LeadDashboard;
